import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { GitGraph, AlertCircle, Copy, Check, Download, Eye, Code } from 'lucide-react';

interface MermaidDiagramProps {
  chart: string;
  isDark?: boolean;
}

export const MermaidDiagram: React.FC<MermaidDiagramProps> = ({ chart, isDark = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showCode, setShowCode] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const diagramId = `mermaid-${Math.random().toString(36).substring(2, 11)}`;

    try {
      mermaid.initialize({
        startOnLoad: false,
        theme: isDark ? 'dark' : 'default',
        securityLevel: 'loose',
        fontFamily: 'Inter, system-ui, sans-serif',
        themeVariables: isDark
          ? {
              primaryColor: '#7c3aed',
              primaryTextColor: '#f4f4f5',
              primaryBorderColor: '#6d28d9',
              lineColor: '#a78bfa',
              secondaryColor: '#1e1b4b',
              tertiaryColor: '#09090b',
              background: '#09090b',
              mainBkg: '#18181b',
              nodeBorder: '#3f3f46',
            }
          : {
              primaryColor: '#6366f1',
              primaryTextColor: '#18181b',
              primaryBorderColor: '#4f46e5',
              lineColor: '#6366f1',
            },
      });

      // Render async
      mermaid
        .render(diagramId, chart.trim())
        .then(({ svg }) => {
          if (isMounted) {
            setSvgContent(svg);
            setError(null);
          }
        })
        .catch((err) => {
          if (isMounted) {
            console.error('Mermaid render error:', err);
            setError(err?.message || 'Не вдалося побудувати діаграму');
          }
        });
    } catch (err: any) {
      if (isMounted) {
        setError(err?.message || 'Помилка ініціалізації Mermaid');
      }
    }

    return () => {
      isMounted = false;
    };
  }, [chart, isDark]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(chart.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadSvg = () => {
    if (!svgContent) return;
    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `diagram-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <div className="my-3 p-3 rounded-xl border border-rose-500/30 bg-rose-950/20 text-rose-300 text-xs font-mono">
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-1.5 font-sans font-semibold text-rose-400">
            <AlertCircle className="w-4 h-4" />
            Помилка синтаксису Mermaid-діаграми
          </span>
          <button
            type="button"
            onClick={handleCopyCode}
            className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
          >
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            Скопіювати код
          </button>
        </div>
        <pre className="p-2 rounded bg-zinc-950/80 overflow-x-auto text-zinc-300 leading-normal">{chart}</pre>
      </div>
    );
  }

  return (
    <div
      className={`my-4 rounded-xl border overflow-hidden shadow-lg transition-all ${
        isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'
      }`}
    >
      {/* Header bar */}
      <div
        className={`flex items-center justify-between px-3.5 py-1.5 text-xs border-b ${
          isDark ? 'bg-zinc-900/90 text-zinc-300 border-zinc-800' : 'bg-zinc-100 text-zinc-700 border-zinc-200'
        }`}
      >
        <span className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[11px] text-violet-400">
          <GitGraph className="w-3.5 h-3.5" />
          Інтерактивна Схема (Mermaid)
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowCode(!showCode)}
            className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 transition-colors"
            title={showCode ? 'Показати діаграму' : 'Показати код схеми'}
          >
            {showCode ? <Eye className="w-3 h-3" /> : <Code className="w-3 h-3" />}
            <span>{showCode ? 'Схема' : 'Вихідний код'}</span>
          </button>
          <button
            type="button"
            onClick={handleCopyCode}
            className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 transition-colors"
            title="Скопіювати синтаксис"
          >
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            <span>{copied ? 'Скопійовано' : 'Копіювати'}</span>
          </button>
          {svgContent && (
            <button
              type="button"
              onClick={handleDownloadSvg}
              className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-violet-900/60 hover:bg-violet-800 text-violet-200 transition-colors"
              title="Завантажити SVG векторне зображення"
            >
              <Download className="w-3 h-3" />
              <span>SVG</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Body */}
      {showCode ? (
        <pre className="p-3.5 text-xs font-mono overflow-x-auto leading-relaxed bg-zinc-950 text-zinc-300">
          <code>{chart}</code>
        </pre>
      ) : (
        <div
          ref={containerRef}
          className="p-4 overflow-x-auto flex items-center justify-center min-h-[140px]"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      )}
    </div>
  );
};

export default MermaidDiagram;
